import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, DEFAULT_LANG, buildLangPath, parseLangPath } from "../utils/langPath";

const BASE_URL = "https://luminawallet.org";

const OG_LOCALES = {
  fr: "fr_FR",
  en: "en_US",
  es: "es_ES",
  de: "de_DE",
  it: "it_IT",
  pt: "pt_PT",
  ru: "ru_RU",
  zh: "zh_CN",
  ar: "ar_AR",
  ja: "ja_JP",
};

const upsertMeta = (selector, attr, key, content) => {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const upsertLink = (rel, href, hreflang) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    if (hreflang) tag.setAttribute('hreflang', hreflang);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
  return tag;
};

const removeAllHreflang = () => {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
};

const SEOHead = ({ title, description, path = "/", pathByLang = null, noindex = false }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || DEFAULT_LANG;
  // `path` is the content path for the CURRENT language (e.g. "/blog/xyz").
  // The current URL is derived by combining current language + content path.
  const { path: contentPath } = parseLangPath(path);
  // Stabilize pathByLang so the effect deps are referentially stable. The
  // serialized JSON is the source of truth for "did the map change?".
  const pathByLangJson = pathByLang ? JSON.stringify(pathByLang) : null;
  const pathByLangStable = useMemo(
    () => (pathByLangJson ? JSON.parse(pathByLangJson) : null),
    [pathByLangJson]
  );
  // Canonical points to the current-language version of this page.
  // When pathByLangStable[currentLang] is provided, prefer that (allows per-language
  // slug overrides such as /en/security instead of /en/securite).
  const canonicalPath = pathByLangStable && pathByLangStable[currentLang]
    ? pathByLangStable[currentLang]
    : contentPath;
  const canonicalHref = `${BASE_URL}${buildLangPath(currentLang, canonicalPath)}`;

  useEffect(() => {
    document.title = title;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);

    // Canonical points to the current-language version of this page.
    upsertLink('canonical', canonicalHref);

    // Rebuild hreflang alternates. When `pathByLangStable` is provided, only emit
    // entries for languages actually listed there — this lets pages with
    // partial localization (e.g. an article translated only in FR + EN) avoid
    // exposing legacy or non-existing versions to search engines.
    removeAllHreflang();
    SUPPORTED_LANGS.forEach((lang) => {
      if (pathByLangStable) {
        const langContentPath = pathByLangStable[lang];
        if (!langContentPath) return;
        const href = `${BASE_URL}${buildLangPath(lang, langContentPath)}`;
        upsertLink('alternate', href, lang);
      } else {
        const href = `${BASE_URL}${buildLangPath(lang, contentPath)}`;
        upsertLink('alternate', href, lang);
      }
    });
    // x-default → French version if available, else fall back to contentPath.
    const defaultPath = pathByLangStable ? pathByLangStable[DEFAULT_LANG] : contentPath;
    if (defaultPath) {
      upsertLink('alternate', `${BASE_URL}${buildLangPath(DEFAULT_LANG, defaultPath)}`, 'x-default');
    }

    document.documentElement.lang = currentLang;
    document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';

    // Open Graph
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonicalHref);
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', OG_LOCALES[currentLang] || 'en_US');

    // Twitter
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    // Robots
    let robotsTag = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robotsTag) {
        robotsTag = document.createElement('meta');
        robotsTag.setAttribute('name', 'robots');
        document.head.appendChild(robotsTag);
      }
      robotsTag.setAttribute('content', 'noindex, nofollow');
    } else if (robotsTag) {
      robotsTag.setAttribute('content', 'index, follow');
    }
  }, [title, description, canonicalHref, contentPath, currentLang, noindex, pathByLangStable]);

  return null;
};

export default SEOHead;
